export type CapacityFilterType = {
  capacityMax?: number;
  capacityMin?: number;
};

type CapacityFilterTypeWithNulls = {
  [K in keyof CapacityFilterType]?: CapacityFilterType[K] | null;
};

/**
 * Class size keys for iteration
 */
export const ClassSizes = ["OneOnOne", "SmallGroup", "LargeGroup"] as const;
export type ClassSize = (typeof ClassSizes)[number];

/**
 * Class size definitions with their corresponding capacity ranges
 */
export const ClassSizeRanges = {
  OneOnOne: { min: 1, max: 1 },
  SmallGroup: { min: 2, max: 8 },
  LargeGroup: { min: 9, max: undefined }, // No upper limit
} as const satisfies Record<
  ClassSize,
  { min: number | undefined; max: number | undefined }
>;

/**
 * Converts capacity filter values to class size strings.
 *
 * @param capacityFilters - Object containing capacityMin and capacityMax values
 * @returns Array of class size strings that match the capacity range
 */
export function getClassSizesFromCapacityFilter(
  capacityFilters?: CapacityFilterTypeWithNulls,
): ClassSize[] {
  // If no capacity filters are provided, return an empty array
  if (
    !capacityFilters ||
    (!capacityFilters?.hasOwnProperty("capacityMin") &&
      !capacityFilters?.hasOwnProperty("capacityMax"))
  ) {
    return [];
  }

  let sizes: ClassSize[] = [];

  // Check each class size range to see if it overlaps with the capacity filter
  for (const classSize of ClassSizes) {
    const range = ClassSizeRanges[classSize as ClassSize];
    const filterMin = capacityFilters.capacityMin ?? 0;
    const filterMax = capacityFilters.capacityMax ?? Number.MAX_VALUE;
    const rangeMin = range.min;
    const rangeMax = range.max ?? Number.MAX_VALUE;

    // Check if the capacity filter range overlaps with this class size range
    if (filterMin <= rangeMax && filterMax >= rangeMin) {
      sizes.push(classSize);
    }
  }

  return sizes;
}

/**
 * Converts class size strings to capacity filter values.
 *
 * @param classSizes - Array of class size strings
 * @returns Capacity filter object with capacityMin and capacityMax
 */
export function getCapacityFiltersFromClassSize(
  classSizes: ClassSize[],
): CapacityFilterType {
  if (classSizes.length === 0) {
    return { capacityMin: undefined, capacityMax: undefined };
  }

  // Check if all class sizes are selected (covers everything)
  if (
    classSizes.length === ClassSizes.length &&
    ClassSizes.every(size => classSizes.includes(size))
  ) {
    return { capacityMin: undefined, capacityMax: undefined };
  }

  // For contiguous selections, determine the min and max capacity
  const hasOneOnOne = classSizes.includes("OneOnOne");
  const hasSmallGroup = classSizes.includes("SmallGroup");
  const hasLargeGroup = classSizes.includes("LargeGroup");

  let capacityMin: number | undefined;
  let capacityMax: number | undefined;

  if (hasOneOnOne && hasSmallGroup && hasLargeGroup) {
    // All three selected - this covers everything
    return { capacityMin: undefined, capacityMax: undefined };
  } else if (hasOneOnOne && hasSmallGroup) {
    // 1-on-1 and small group
    capacityMin = undefined;
    capacityMax = ClassSizeRanges.SmallGroup.max;
  } else if (hasSmallGroup && hasLargeGroup) {
    // Small group and large group
    capacityMin = ClassSizeRanges.SmallGroup.min;
    capacityMax = undefined;
  } else if (hasOneOnOne) {
    // Just 1-on-1
    capacityMin = undefined;
    capacityMax = ClassSizeRanges.OneOnOne.max;
  } else if (hasSmallGroup) {
    // Just small group
    capacityMin = ClassSizeRanges.SmallGroup.min;
    capacityMax = ClassSizeRanges.SmallGroup.max;
  } else if (hasLargeGroup) {
    // Just large group
    capacityMin = ClassSizeRanges.LargeGroup.min;
    capacityMax = undefined;
  }

  return { capacityMin, capacityMax };
}
